/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import EffectSequence from '../../../Effects/EffectSequence';
import CLog, { LogTypes } from '../../ComponentLogger';
import ClassUtil from '../../ClassUtil';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Button used to run [[cc.Animation]] or [[EffectSequence]].
 */
@cc._decorator.ccclass
export default class AnimationButton extends cc.Button
{
    public static readonly OnCompleteEventName: string = 'AnimationButton.OnComplete';
    public static readonly ClickHandlerEventName: string = 'AnimationButton.ClickHandler';

    @property(cc.Animation)
    protected animation: cc.Animation = null;
    public set Animation(value: cc.Animation) { this.animation = value; this.updateLabel(); }

    @property(EffectSequence)
    protected effectSequence: EffectSequence = null;
    public set EffectSequence(value: EffectSequence) { this.effectSequence = value; this.updateLabel(); }

    @property(cc.color)
    protected executingColor: cc.Color = cc.Color.BLUE;
    private _startingColors: any =
    {
        normalColor: this.normalColor,
        hoverColor: this.hoverColor
    };

    private _lastAnimState: cc.AnimationState = null;
    private _runningCount: number = 0;

    /** @hidden */
    protected onLoad(): void
    {
        if (CC_EDITOR) { return; }

        this.createHandler();
    }

    private createHandler(): void
    {
        let eventName: string = AnimationButton.ClickHandlerEventName;

        // Create Slider Handler
        let sliderHandler: cc.Component.EventHandler = new cc.Component.EventHandler();
        sliderHandler.component = ClassUtil.getName(AnimationButton);
        sliderHandler.target = this.node;
        sliderHandler.handler = eventName;

        if (this[eventName])
        {
            CLog.error(`Component already has property [ ${eventName} ]`,
                this, LogTypes.Initialization);
        }
        this[eventName] = this.onClickHandler.bind(this);

        this.clickEvents.push(sliderHandler);

        this.updateLabel();
    }

    private onClickHandler = (event: cc.Event.EventCustom) =>
    {
        if (this.animation) { this.animHandler(); }
        else if (this.effectSequence) { this.effectSequenceHandler(); }
    }

    private animHandler(): void
    {
        this.updateLabel();

        if (!this._lastAnimState)
        {
            this._lastAnimState = this.animation.play();

            this.animation.once('finished', () =>
            {
                if (--this._runningCount > 0) { return; }
                this._lastAnimState = null;
            });
            return;
        }

        if (this._lastAnimState.isPlaying)
        {
            this.animation.pause();
        }
        else if (this._lastAnimState.isPaused)
        {
            this.animation.resume();
        }

        this._runningCount++;
    }

    private effectSequenceHandler(): void
    {
        this.updateLabel();

        if (this.effectSequence.StateManager.IsStarted) { return; }

        this.normalColor = this.executingColor;
        this.hoverColor = this.executingColor;

        this.effectSequence.OnComplete(() =>
        {
            if (--this._runningCount > 0) { return; }

            this.normalColor = this._startingColors.normalColor;
            this.hoverColor = this._startingColors.hoverColor;

            this.node.dispatchEvent(new cc.Event.EventCustom(AnimationButton.OnCompleteEventName, false));
        });

        this.effectSequence.Trigger();
        this._runningCount++;
    }

    /** Update button label child component. */
    private updateLabel(): void
    {
        let toRunName: string = this.animation
            ? this.animation ? this.animation.name : ''
            : this.effectSequence ? this.effectSequence.node.name : '';
        this.getComponentInChildren(cc.Label).string = `Run: ${toRunName}`;
    }
}
